import { httpsCallable } from "firebase/functions";
import { auth, functions } from "@/firebase/authorityClient";
import { isOwnWalletDetailFeatureEnabled } from "@/config/runtimeConfig";
import {
  UNIT_AMOUNT_SCALE,
  isCanonicalNonnegativeAtomic,
} from "@/utils/atomicCurrencyFormatting";
import { withUiDeadline } from "@/utils/uiDeadline";
import { walletStatementEntryKey } from "@/utils/walletStatement";

const SCHEMA_VERSION = 1;
const STATEMENT_PAGE_SIZE = 20;
const ORG_ID_PATTERN = /^[A-Za-z0-9][A-Za-z0-9_-]{0,127}$/;
const CURRENCY_CODE_PATTERN = /^[A-Z0-9][A-Z0-9._-]{0,15}$/;
const SETTLEMENT_REF_PATTERN = /^[a-f0-9]{64}$/;
const CURSOR_PATTERN = /^[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+$/;
const POSITIVE_ATOMIC_PATTERN = /^[1-9][0-9]*$/;
const CONTROL_CHARACTERS =
  /[\u0000-\u001f\u007f-\u009f\u061c\u200e\u200f\u202a-\u202e\u2066-\u2069]/;
const WALLET_STATUSES = new Set(["ready", "setup-pending"]);
const STATEMENT_KINDS = new Set(["listing-purchase", "unit-transfer"]);
const STATEMENT_DIRECTIONS = new Set(["debit", "credit"]);
const STATEMENT_V3_SCHEMA_VERSION = 3;
// One movement can be returned as two rows: both sides of a Savings move, or
// a receipt split between Current and Savings by the Current account limit.
const STATEMENT_V3_MAX_ROWS = STATEMENT_PAGE_SIZE * 2;
const PUBLIC_IDENTIFIER_PATTERN = /^[a-z0-9][a-z0-9_-]{2,63}$/;
const MAX_TIMESTAMP_MS = 8_640_000_000_000_000;
const ENTRY_ACCOUNTS = new Set(["current", "savings"]);
const ENTRY_DIRECTIONS = new Set(["in", "out"]);
const ENTRY_KINDS = new Set([
  "transfer",
  "purchase",
  "savings-move",
  "expiry",
  "income",
]);
const INCOME_COMPONENTS = new Set(["basic-income", "salary", "catch-up"]);

const DETAIL_KEYS = Object.freeze([
  "schemaVersion",
  "organisation",
  "wallet",
  "statement",
  "nextCursor",
  "hasMore",
]);
const ORGANISATION_KEYS = Object.freeze(["orgId", "name", "logo", "currency"]);
const WALLET_KEYS = Object.freeze(["walletStatus", "balances"]);
const BALANCE_KEYS = Object.freeze([
  "amountScale",
  "currentAvailableAtomic",
  "savingsAvailableAtomic",
  "totalAvailableAtomic",
]);
const STATEMENT_V3_KEYS = Object.freeze([
  "schemaVersion",
  "organisation",
  "wallet",
  "entries",
  "nextCursor",
  "hasMore",
]);
const ENTRY_KEYS = Object.freeze([
  "entryRef",
  "account",
  "direction",
  "kind",
  "amountAtomic",
  "amountScale",
  "balanceAfterAtomic",
  "createdAt",
  "counterparty",
  "detail",
]);
const COUNTERPARTY_KEYS = Object.freeze([
  "displayName",
  "publicIdentifier",
  "organisation",
]);
const STATEMENT_KEYS = Object.freeze([
  "settlementRef",
  "kind",
  "direction",
  "amountAtomic",
  "amountScale",
  "createdAt",
]);

export class WalletDetailAuthorityServiceError extends Error {
  constructor(code = "unknown", message = "Wallet details are unavailable.") {
    super(message);
    this.name = "WalletDetailAuthorityServiceError";
    this.code = code;
  }
}

function fail(code, message = "Wallet details are unavailable.") {
  throw new WalletDetailAuthorityServiceError(code, message);
}

function isPlainRecord(value) {
  if (!value || typeof value !== "object" || Array.isArray(value)) return false;
  const prototype = Object.getPrototypeOf(value);
  return prototype === Object.prototype || prototype === null;
}

function hasExactKeys(value, expectedKeys) {
  if (!isPlainRecord(value)) return false;
  const ownKeys = Reflect.ownKeys(value);
  const descriptors = Object.getOwnPropertyDescriptors(value);
  const actualKeys = ownKeys.filter((key) => typeof key === "string").sort();
  const sortedExpectedKeys = [...expectedKeys].sort();
  return (
    ownKeys.every(
      (key) =>
        typeof key === "string" &&
        descriptors[key]?.enumerable === true &&
        Object.prototype.hasOwnProperty.call(descriptors[key], "value"),
    ) &&
    actualKeys.length === sortedExpectedKeys.length &&
    actualKeys.every((key, index) => key === sortedExpectedKeys[index])
  );
}

function normalizeOrgId(value, code = "invalid-argument") {
  if (typeof value !== "string" || !ORG_ID_PATTERN.test(value)) fail(code);
  return value;
}

function normalizeCursor(value, code = "invalid-argument") {
  if (value === null) return null;
  if (
    typeof value !== "string" ||
    value.length < 32 ||
    value.length > 2048 ||
    !CURSOR_PATTERN.test(value)
  ) {
    fail(code);
  }
  return value;
}

function normalizeText(
  value,
  { minimum = 1, maximum, code = "invalid-response" } = {},
) {
  if (
    typeof value !== "string" ||
    value !== value.trim() ||
    value.length < minimum ||
    value.length > maximum ||
    CONTROL_CHARACTERS.test(value)
  ) {
    fail(code);
  }
  return value;
}

function normalizeCurrency(value) {
  if (!hasExactKeys(value, ["name", "symbol", "code"])) {
    fail("invalid-response");
  }
  const name = normalizeText(value.name, { maximum: 40 });
  const symbol = normalizeText(value.symbol, { maximum: 16 });
  if (
    Array.from(symbol).length > 8 ||
    typeof value.code !== "string" ||
    !CURRENCY_CODE_PATTERN.test(value.code)
  ) {
    fail("invalid-response");
  }
  return Object.freeze({ name, symbol, code: value.code });
}

function normalizeBalances(value, walletStatus) {
  if (walletStatus !== "ready") {
    if (value !== null) fail("invalid-response");
    return null;
  }
  if (
    !hasExactKeys(value, BALANCE_KEYS) ||
    value.amountScale !== UNIT_AMOUNT_SCALE ||
    !isCanonicalNonnegativeAtomic(value.currentAvailableAtomic) ||
    !isCanonicalNonnegativeAtomic(value.savingsAvailableAtomic) ||
    !isCanonicalNonnegativeAtomic(value.totalAvailableAtomic) ||
    BigInt(value.currentAvailableAtomic) + BigInt(value.savingsAvailableAtomic) !==
      BigInt(value.totalAvailableAtomic)
  ) {
    fail("invalid-response");
  }
  return Object.freeze({
    amountScale: UNIT_AMOUNT_SCALE,
    currentAvailableAtomic: value.currentAvailableAtomic,
    savingsAvailableAtomic: value.savingsAvailableAtomic,
    totalAvailableAtomic: value.totalAvailableAtomic,
  });
}

function normalizeStatementEntry(value) {
  if (
    !hasExactKeys(value, STATEMENT_KEYS) ||
    typeof value.settlementRef !== "string" ||
    !SETTLEMENT_REF_PATTERN.test(value.settlementRef) ||
    !STATEMENT_KINDS.has(value.kind) ||
    !STATEMENT_DIRECTIONS.has(value.direction) ||
    typeof value.amountAtomic !== "string" ||
    value.amountAtomic.length > 60 ||
    !POSITIVE_ATOMIC_PATTERN.test(value.amountAtomic) ||
    value.amountScale !== UNIT_AMOUNT_SCALE ||
    !Number.isSafeInteger(value.createdAt) ||
    value.createdAt < 0
  ) {
    fail("invalid-response");
  }
  return Object.freeze({
    settlementRef: value.settlementRef,
    kind: value.kind,
    direction: value.direction,
    amountAtomic: value.amountAtomic,
    amountScale: UNIT_AMOUNT_SCALE,
    createdAt: value.createdAt,
  });
}

function normalizeOrganisation(value, request) {
  if (!hasExactKeys(value, ORGANISATION_KEYS)) fail("invalid-response");
  const orgId = normalizeOrgId(value.orgId, "invalid-response");
  const name = normalizeText(value.name, { minimum: 2, maximum: 80 });
  if (orgId !== request.orgId || value.logo !== null) fail("invalid-response");
  return Object.freeze({
    orgId,
    name,
    logo: null,
    currency: normalizeCurrency(value.currency),
  });
}

function normalizeWallet(value) {
  if (
    !hasExactKeys(value, WALLET_KEYS) ||
    !WALLET_STATUSES.has(value.walletStatus)
  ) {
    fail("invalid-response");
  }
  return Object.freeze({
    walletStatus: value.walletStatus,
    balances: normalizeBalances(value.balances, value.walletStatus),
  });
}

function normalizeDetail(value, request) {
  if (
    !hasExactKeys(value, DETAIL_KEYS) ||
    value.schemaVersion !== SCHEMA_VERSION ||
    !Array.isArray(value.statement) ||
    value.statement.length > STATEMENT_PAGE_SIZE ||
    typeof value.hasMore !== "boolean"
  ) {
    fail("invalid-response");
  }

  const organisation = normalizeOrganisation(value.organisation, request);
  const wallet = normalizeWallet(value.wallet);
  const statement = value.statement.map(normalizeStatementEntry);
  const settlementRefs = statement.map(({ settlementRef }) => settlementRef);
  const nextCursor = normalizeCursor(value.nextCursor, "invalid-response");
  if (
    new Set(settlementRefs).size !== settlementRefs.length ||
    value.hasMore !== (nextCursor !== null) ||
    (value.hasMore && statement.length !== STATEMENT_PAGE_SIZE) ||
    (request.cursor !== null && request.cursor === nextCursor) ||
    (wallet.walletStatus !== "ready" &&
      (statement.length !== 0 || nextCursor !== null || value.hasMore))
  ) {
    fail("invalid-response");
  }

  return Object.freeze({
    schemaVersion: SCHEMA_VERSION,
    organisation,
    wallet,
    statement: Object.freeze(statement),
    nextCursor,
    hasMore: value.hasMore,
  });
}

function normalizeCounterparty(value) {
  if (value === null) return null;
  if (!hasExactKeys(value, COUNTERPARTY_KEYS)) fail("invalid-response");
  if (
    value.publicIdentifier !== null &&
    (typeof value.publicIdentifier !== "string" ||
      !PUBLIC_IDENTIFIER_PATTERN.test(value.publicIdentifier))
  ) {
    fail("invalid-response");
  }
  let organisation = null;
  if (value.organisation !== null) {
    if (!hasExactKeys(value.organisation, ["orgId", "name"])) {
      fail("invalid-response");
    }
    organisation = Object.freeze({
      orgId: normalizeOrgId(value.organisation.orgId, "invalid-response"),
      name: normalizeText(value.organisation.name, { maximum: 120 }),
    });
  }
  return Object.freeze({
    // A null name is a deleted account, never an empty string.
    displayName:
      value.displayName === null
        ? null
        : normalizeText(value.displayName, { maximum: 80 }),
    publicIdentifier: value.publicIdentifier,
    organisation,
  });
}

function normalizeEntryDetail(value) {
  if (value === null) return null;
  if (
    !hasExactKeys(value, ["component", "roleTitle"]) ||
    !INCOME_COMPONENTS.has(value.component)
  ) {
    fail("invalid-response");
  }
  return Object.freeze({
    component: value.component,
    roleTitle:
      value.roleTitle === null
        ? null
        : normalizeText(value.roleTitle, { maximum: 100 }),
  });
}

function normalizeEntry(value) {
  if (
    !hasExactKeys(value, ENTRY_KEYS) ||
    typeof value.entryRef !== "string" ||
    !SETTLEMENT_REF_PATTERN.test(value.entryRef) ||
    !ENTRY_ACCOUNTS.has(value.account) ||
    !ENTRY_DIRECTIONS.has(value.direction) ||
    !ENTRY_KINDS.has(value.kind) ||
    typeof value.amountAtomic !== "string" ||
    value.amountAtomic.length > 60 ||
    !POSITIVE_ATOMIC_PATTERN.test(value.amountAtomic) ||
    value.amountScale !== UNIT_AMOUNT_SCALE ||
    !isCanonicalNonnegativeAtomic(value.balanceAfterAtomic) ||
    !Number.isSafeInteger(value.createdAt) ||
    value.createdAt < 0 ||
    value.createdAt > MAX_TIMESTAMP_MS
  ) {
    fail("invalid-response");
  }
  return Object.freeze({
    entryRef: value.entryRef,
    account: value.account,
    direction: value.direction,
    kind: value.kind,
    amountAtomic: value.amountAtomic,
    amountScale: UNIT_AMOUNT_SCALE,
    balanceAfterAtomic: value.balanceAfterAtomic,
    createdAt: value.createdAt,
    counterparty: normalizeCounterparty(value.counterparty),
    detail: normalizeEntryDetail(value.detail),
  });
}

function normalizeStatementV3(value, request) {
  if (
    !hasExactKeys(value, STATEMENT_V3_KEYS) ||
    value.schemaVersion !== STATEMENT_V3_SCHEMA_VERSION ||
    !Array.isArray(value.entries) ||
    value.entries.length > STATEMENT_V3_MAX_ROWS ||
    typeof value.hasMore !== "boolean"
  ) {
    fail("invalid-response");
  }

  const organisation = normalizeOrganisation(value.organisation, request);
  const wallet = normalizeWallet(value.wallet);
  const entries = value.entries.map(normalizeEntry);
  const entryKeys = entries.map(walletStatementEntryKey);
  const nextCursor = normalizeCursor(value.nextCursor, "invalid-response");
  if (
    new Set(entryKeys).size !== entryKeys.length ||
    value.hasMore !== (nextCursor !== null) ||
    (value.hasMore && entries.length === 0) ||
    (request.cursor !== null && request.cursor === nextCursor) ||
    (wallet.walletStatus !== "ready" &&
      (entries.length !== 0 || nextCursor !== null || value.hasMore))
  ) {
    fail("invalid-response");
  }

  return Object.freeze({
    schemaVersion: STATEMENT_V3_SCHEMA_VERSION,
    organisation,
    wallet,
    entries: Object.freeze(entries),
    nextCursor,
    hasMore: value.hasMore,
  });
}

function normalizeServiceError(error) {
  if (error instanceof WalletDetailAuthorityServiceError) return error;
  const messages = {
    "deadline-exceeded": "Wallet details did not respond in time.",
    "failed-precondition": "This Wallet is temporarily unavailable.",
    "invalid-argument": "The Wallet request was invalid.",
    "permission-denied": "You do not have access to this Wallet.",
    "stale-auth-context": "The signed-in account changed while loading the Wallet.",
    "ui-deadline": "Wallet details did not respond in time.",
    unauthenticated: "Sign in again to view this Wallet.",
    unavailable: "Wallet details are temporarily unavailable.",
  };
  const candidateCode = String(error?.code || "unknown").split("/").pop();
  const code = Object.hasOwn(messages, candidateCode) ? candidateCode : "unknown";
  return new WalletDetailAuthorityServiceError(code, messages[code]);
}

function requireActor(authInstance) {
  const actor = authInstance?.currentUser;
  if (
    !actor ||
    typeof actor.uid !== "string" ||
    actor.uid.length < 1 ||
    actor.uid.length > 128 ||
    CONTROL_CHARACTERS.test(actor.uid)
  ) {
    fail("unauthenticated", "Sign in again to view this Wallet.");
  }
  return actor;
}

export function createWalletDetailAuthorityService({
  authInstance = auth,
  functionsInstance = functions,
  callableFactory = httpsCallable,
  enabled = isOwnWalletDetailFeatureEnabled,
} = {}) {
  let detailCallable;

  return Object.freeze({
    async getOwnLegacyWalletHistory({ orgId, account, cursor = null }) {
      if (!enabled) fail("feature-disabled");
      const request = { schemaVersion: 2, orgId: normalizeOrgId(orgId), account,
        cursor: normalizeCursor(cursor), limit: STATEMENT_PAGE_SIZE };
      if (!["current", "savings"].includes(account)) fail("invalid-argument");
      const expectedActor = requireActor(authInstance);
      detailCallable ||= callableFactory(functionsInstance, "getOwnWalletDetail");
      try {
        const response = await withUiDeadline(detailCallable(request),
          { phase: "loading historical statements", timeoutMs: 15_000 });
        if (authInstance?.currentUser !== expectedActor) fail("stale-auth-context");
        return normalizeLegacyHistory(response?.data, request);
      } catch (error) {
        throw normalizeServiceError(error);
      }
    },
    async getOwnWalletDetail(optionsValue) {
      if (!enabled) fail("feature-disabled");
      if (!hasExactKeys(optionsValue, ["orgId", "cursor"])) {
        fail("invalid-argument");
      }
      const request = Object.freeze({
        orgId: normalizeOrgId(optionsValue.orgId),
        cursor: normalizeCursor(optionsValue.cursor),
      });
      const expectedActor = requireActor(authInstance);
      if (!detailCallable) {
        detailCallable = callableFactory(functionsInstance, "getOwnWalletDetail");
      }

      try {
        const response = await withUiDeadline(
          detailCallable({
            schemaVersion: SCHEMA_VERSION,
            orgId: request.orgId,
            cursor: request.cursor,
            limit: STATEMENT_PAGE_SIZE,
          }),
          { phase: "loading Wallet details", timeoutMs: 15_000 },
        );
        if (authInstance?.currentUser !== expectedActor) {
          fail("stale-auth-context");
        }
        return normalizeDetail(response?.data, request);
      } catch (error) {
        throw normalizeServiceError(error);
      }
    },
    // Request version 3 of the same callable: balances plus one combined
    // statement of every movement in both accounts, newest first.
    async getOwnWalletStatement(optionsValue) {
      if (!enabled) fail("feature-disabled");
      if (!hasExactKeys(optionsValue, ["orgId", "cursor"])) {
        fail("invalid-argument");
      }
      const request = Object.freeze({
        orgId: normalizeOrgId(optionsValue.orgId),
        cursor: normalizeCursor(optionsValue.cursor),
      });
      const expectedActor = requireActor(authInstance);
      detailCallable ||= callableFactory(functionsInstance, "getOwnWalletDetail");

      try {
        const response = await withUiDeadline(
          detailCallable({
            schemaVersion: STATEMENT_V3_SCHEMA_VERSION,
            orgId: request.orgId,
            cursor: request.cursor,
            limit: STATEMENT_PAGE_SIZE,
          }),
          { phase: "loading the Wallet statement", timeoutMs: 15_000 },
        );
        if (authInstance?.currentUser !== expectedActor) {
          fail("stale-auth-context");
        }
        return normalizeStatementV3(response?.data, request);
      } catch (error) {
        throw normalizeServiceError(error);
      }
    },
  });
}

export function normalizeLegacyHistory(value, request) {
  const labels = new Set(["Basic income", "Salary", "Purchase", "Sale", "Transfer", "Clap",
    "Savings transfer", "Recorded removal", "Ad hoc income", "Recorded activity"]);
  if (!hasExactKeys(value, ["schemaVersion", "orgId", "account", "provenance", "sourceStatus",
    "snapshotRef", "entries", "omittedCount", "hasMore", "nextCursor"]) || value.schemaVersion !== 2 ||
    value.orgId !== request.orgId || value.account !== request.account || value.provenance !== "legacy-unverified" ||
    !["available", "not-found"].includes(value.sourceStatus) || !SETTLEMENT_REF_PATTERN.test(value.snapshotRef) ||
    !Array.isArray(value.entries) || value.entries.length > STATEMENT_PAGE_SIZE ||
    !Number.isSafeInteger(value.omittedCount) || value.omittedCount < 0 || value.omittedCount > 10000 ||
    typeof value.hasMore !== "boolean") fail("invalid-response");
  const entries = value.entries.map((row) => {
    if (!hasExactKeys(row, ["entryRef", "direction", "amount", "createdAt", "label"]) ||
      !SETTLEMENT_REF_PATTERN.test(row.entryRef) || !STATEMENT_DIRECTIONS.has(row.direction) ||
      typeof row.amount !== "string" || row.amount.length > 122 || !/^(0|[1-9]\d*)(\.\d+)?$/.test(row.amount) ||
      !Number.isSafeInteger(row.createdAt) || row.createdAt < 0 || row.createdAt > 8640000000000000 ||
      !labels.has(row.label)) fail("invalid-response");
    return Object.freeze({ ...row });
  });
  const nextCursor = normalizeCursor(value.nextCursor, "invalid-response");
  if (value.hasMore !== (nextCursor !== null) || (value.hasMore && entries.length !== STATEMENT_PAGE_SIZE) ||
    (nextCursor !== null && nextCursor === request.cursor) ||
    new Set(entries.map((row) => row.entryRef)).size !== entries.length ||
    (value.sourceStatus === "not-found" && (entries.length || value.omittedCount || value.hasMore))) fail("invalid-response");
  return Object.freeze({ ...value, entries: Object.freeze(entries), nextCursor });
}

export const walletDetailAuthorityService =
  createWalletDetailAuthorityService();
